// =============================================================================
// Admin: invite a new partner from the app — the same flow as the website
// Partner Dashboard's admin portal (Tonyte_website/src/adminPartnersStore.js
// adminCreatePartner). createPartnerInvitationEu re-checks admin rights,
// creates the account + partner profile (status 'invited'), allocates the
// Partner ID and sends the activation email. Once the partner sets their
// password, their Vibe at Venues profile is created automatically
// (functions/partnerVenues.js).
// =============================================================================
import React, { useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StatusBar,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import TonyteBackground from '../components/TonyteBackground';
import { auth, firestore, functionsEu, httpsCallable } from '../firebase';
import { useProfile } from '../hooks/useProfile';
import { Radius, Typography } from '../theme';
import { useTheme } from '../theme/useTheme';
import { daylightStyle } from '../theme/daylightStyle';
import type { TonyteTheme } from '../theme/types';
import type { RootStackParamList } from '../types';

type Props = NativeStackScreenProps<RootStackParamList, 'AddPartner'>;

// Same types and order as the website admin portal.
const PARTNER_TYPES: { key: string; label: string }[] = [
  { key: 'venue', label: 'Venue' },
  { key: 'organizer', label: 'Organizer' },
  { key: 'community', label: 'Community' },
  { key: 'university', label: 'University' },
  { key: 'student_organization', label: 'Student Organization' },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function AddPartnerScreen({ navigation }: Props) {
  const insets = useSafeAreaInsets();
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);
  const userId = auth.currentUser?.uid ?? null;
  const { profile } = useProfile(userId);
  const isAdmin = profile?.role === 'admin' || profile?.role === 'co-admin';

  const [organizationName, setOrganizationName] = useState('');
  const [email, setEmail] = useState('');
  const [partnerType, setPartnerType] = useState('');
  const [city, setCity] = useState('Dresden');
  const [website, setWebsite] = useState('');
  const [instagram, setInstagram] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState<{ partnerId: string; email: string; name: string } | null>(null);

  const trimmedEmail = email.trim().toLowerCase();
  const valid = organizationName.trim().length > 0 && EMAIL_PATTERN.test(trimmedEmail) && Boolean(partnerType);

  const reset = () => {
    setOrganizationName('');
    setEmail('');
    setPartnerType('');
    setCity('Dresden');
    setWebsite('');
    setInstagram('');
    setDescription('');
    setCreated(null);
  };

  const submit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      await auth.currentUser?.getIdToken(true);
      const invite = httpsCallable<
        { email: string; organizationName: string; partnerRole: string; city: string },
        { uid: string; partnerId: string }
      >(functionsEu, 'createPartnerInvitationEu');
      const { data } = await invite({
        email: trimmedEmail,
        organizationName: organizationName.trim(),
        partnerRole: partnerType,
        city: city.trim(),
      });
      // Optional public details, saved the same way the website does.
      const extras: Record<string, unknown> = {};
      if (website.trim()) extras.website = website.trim();
      if (instagram.trim()) extras.instagram = instagram.trim();
      if (description.trim()) extras.description = description.trim();
      if (Object.keys(extras).length) {
        await setDoc(
          doc(firestore, 'partnerProfiles', data.uid),
          { ...extras, updatedAt: serverTimestamp(), updatedBy: userId },
          { merge: true },
        );
      }
      setCreated({ partnerId: data.partnerId, email: trimmedEmail, name: organizationName.trim() });
    } catch (error) {
      // The server's messages are written for admins (e.g. "An activation
      // invitation is already pending for this email…").
      const message = (error as { message?: string })?.message;
      Alert.alert('Could not add partner', message && message !== 'internal' ? message : 'Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const iconColor = theme.isDark ? '#fff' : theme.colors.icon;
  const placeholder = theme.isDark ? 'rgba(255,255,255,0.4)' : theme.colors.textPlaceholder;
  const field = (label: string, value: string, onChange: (v: string) => void, options: Partial<React.ComponentProps<typeof TextInput>> = {}, required = false) => (
    <View style={styles.field}>
      <Text style={[styles.label, appearance.textSecondary]}>{label}{required ? <Text style={styles.required}>  Required</Text> : null}</Text>
      <TextInput
        style={[styles.input, appearance.input, options.multiline && styles.multiline]}
        value={value}
        onChangeText={onChange}
        placeholderTextColor={placeholder}
        editable={!submitting}
        {...options}
      />
    </View>
  );

  return (
    <TonyteBackground appearanceAware>
      <StatusBar barStyle={theme.statusBarStyle} translucent backgroundColor="transparent" />
      <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <View style={[styles.header, { paddingTop: insets.top + 12 }]}>
          <TouchableOpacity style={[styles.iconButton, appearance.control]} onPress={() => navigation.goBack()}>
            <Icon name="chevron-left" size={22} color={iconColor} />
          </TouchableOpacity>
          <View style={styles.flex}>
            <Text style={[styles.kicker, appearance.kicker]}>ADMIN</Text>
            <Text style={[styles.title, appearance.textPrimary]}>Add partner</Text>
          </View>
        </View>

        {!isAdmin ? (
          <Text style={[styles.message, appearance.textSecondary]}>{profile ? 'Only Tonyte admins can add partners.' : 'Loading…'}</Text>
        ) : created ? (
          <View style={styles.success}>
            <View style={styles.successIcon}><Icon name="send" size={26} color="#FF9A3C" /></View>
            <Text style={[styles.successTitle, appearance.textPrimary]}>Invitation sent</Text>
            <Text style={[styles.successText, appearance.textSecondary]}>
              {created.name} ({created.email}) will get an activation email. Their partner profile appears in Vibe at Venues as soon as they set their password.
            </Text>
            <View style={[styles.idPill, appearance.control]}>
              <Text style={[styles.idLabel, appearance.textSecondary]}>Partner ID</Text>
              <Text style={[styles.idValue, appearance.textPrimary]} selectable>{created.partnerId}</Text>
            </View>
            <TouchableOpacity style={styles.primaryButton} onPress={reset}>
              <Text style={styles.primaryText}>Add another partner</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.secondaryButton, appearance.control]} onPress={() => navigation.goBack()}>
              <Text style={[styles.secondaryText, appearance.textPrimary]}>Done</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <ScrollView contentContainerStyle={[styles.scroll, { paddingBottom: insets.bottom + 40 }]} keyboardShouldPersistTaps="handled">
            <Text style={[styles.intro, appearance.textSecondary]}>
              Creates the partner account and emails them an activation link. They set their own password; their profile then shows up in Vibe at Venues automatically.
            </Text>
            {field('Organisation name', organizationName, setOrganizationName, { placeholder: 'e.g. InterLokal – Social Hub' }, true)}
            {field('Email', email, setEmail, { placeholder: 'partner@example.com', keyboardType: 'email-address', autoCapitalize: 'none', autoCorrect: false }, true)}
            <View style={styles.field}>
              <Text style={[styles.label, appearance.textSecondary]}>Partner type<Text style={styles.required}>  Required</Text></Text>
              <View style={styles.chips}>
                {PARTNER_TYPES.map(type => (
                  <TouchableOpacity
                    key={type.key}
                    onPress={() => setPartnerType(type.key)}
                    disabled={submitting}
                    style={[styles.chip, appearance.chip, partnerType === type.key && styles.chipActive, partnerType === type.key && appearance.chipActive]}
                  >
                    <Text style={[styles.chipText, appearance.textSecondary, partnerType === type.key && styles.chipTextActive, partnerType === type.key && appearance.textPrimary]}>{type.label}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            {field('City', city, setCity, { placeholder: 'Dresden' })}
            {field('Website', website, setWebsite, { placeholder: 'https://', keyboardType: 'url', autoCapitalize: 'none', autoCorrect: false })}
            {field('Instagram', instagram, setInstagram, { placeholder: 'https://instagram.com/…', keyboardType: 'url', autoCapitalize: 'none', autoCorrect: false })}
            {field('Description', description, setDescription, { placeholder: 'What they do — becomes their profile bio', multiline: true, maxLength: 600 })}
            <TouchableOpacity style={[styles.primaryButton, (!valid || submitting) && styles.disabled]} disabled={!valid || submitting} onPress={() => void submit()}>
              {submitting ? <ActivityIndicator color="#fff" /> : <Text style={styles.primaryText}>Send invitation</Text>}
            </TouchableOpacity>
          </ScrollView>
        )}
      </KeyboardAvoidingView>
    </TonyteBackground>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  header: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingBottom: 8 },
  iconButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  kicker: { color: '#FF9A3C', fontSize: 11, fontWeight: Typography.extrabold, letterSpacing: 1.6 },
  title: { color: '#fff', fontSize: 24, fontWeight: Typography.extrabold, marginTop: 2 },
  message: { color: 'rgba(255,255,255,0.62)', fontSize: 14, textAlign: 'center', padding: 24 },
  scroll: { paddingHorizontal: 16 },
  intro: { color: 'rgba(255,255,255,0.62)', fontSize: 13, lineHeight: 19, marginBottom: 6 },
  field: { marginTop: 14 },
  label: { color: 'rgba(255,255,255,0.7)', fontSize: 13, fontWeight: Typography.bold, marginBottom: 7 },
  required: { color: '#FF9A3C', fontSize: 11, fontWeight: Typography.bold },
  input: {
    minHeight: 46,
    borderRadius: 13,
    paddingHorizontal: 12,
    paddingVertical: 11,
    color: '#fff',
    fontSize: 14,
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  multiline: { minHeight: 100, textAlignVertical: 'top' },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  chip: {
    height: 34,
    paddingHorizontal: 14,
    borderRadius: Radius.pill,
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  chipActive: { backgroundColor: 'rgba(242,125,38,0.2)', borderColor: 'rgba(255,154,60,0.55)' },
  chipText: { color: 'rgba(255,255,255,0.68)', fontSize: 12, fontWeight: Typography.bold },
  chipTextActive: { color: '#fff' },
  primaryButton: { marginTop: 24, height: 50, borderRadius: 16, alignItems: 'center', justifyContent: 'center', backgroundColor: '#F27D26' },
  primaryText: { color: '#fff', fontSize: 15, fontWeight: Typography.extrabold },
  disabled: { opacity: 0.45 },
  success: { paddingHorizontal: 24, paddingTop: 36, alignItems: 'center' },
  successIcon: { width: 64, height: 64, borderRadius: 32, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,122,0,0.15)' },
  successTitle: { color: '#fff', fontSize: 22, fontWeight: Typography.extrabold, marginTop: 16 },
  successText: { color: 'rgba(255,255,255,0.65)', fontSize: 14, lineHeight: 20, textAlign: 'center', marginTop: 8 },
  idPill: {
    marginTop: 18,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 14,
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  idLabel: { color: 'rgba(255,255,255,0.55)', fontSize: 11 },
  idValue: { color: '#fff', fontSize: 16, fontWeight: Typography.extrabold, marginTop: 2, letterSpacing: 0.5 },
  secondaryButton: {
    alignSelf: 'stretch',
    marginTop: 10,
    height: 48,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  secondaryText: { color: '#fff', fontSize: 15, fontWeight: Typography.bold },
});

// Midnight stays the canonical StyleSheet above; Daylight only overrides.
const createThemeStyles = (theme: TonyteTheme) => {
  const c = theme.colors;
  return StyleSheet.create({
    control: daylightStyle(theme, { backgroundColor: c.controlBackground, borderColor: c.border }),
    input: daylightStyle(theme, { backgroundColor: c.inputBackground, borderColor: c.border, color: c.textPrimary }),
    chip: daylightStyle(theme, { backgroundColor: c.chipBackground, borderColor: c.border }),
    chipActive: daylightStyle(theme, { backgroundColor: c.chipBackgroundActive, borderColor: 'rgba(242,125,38,0.36)' }),
    kicker: daylightStyle(theme, { color: c.filterActiveIcon }),
    textPrimary: daylightStyle(theme, { color: c.textPrimary }),
    textSecondary: daylightStyle(theme, { color: c.textSecondary }),
  });
};
